import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  CheckCircle2, Minus, Plus, Calendar, ChevronLeft, ChevronRight,
  MapPin, Loader2, ShieldCheck,
} from "lucide-react";
import api, { externalRedirect } from "@/lib/api";
import { PLANS, planById } from "@/data/plans";
import { useAuth, fmtErr } from "@/context/AuthContext";

const STEP_LABELS = ["Plan", "Your Details", "Time Slot", "Payment"];

export default function Booking() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const referralCode = params.get("ref") || "";
  const [step, setStep] = useState(0);
  const [plan, setPlan] = useState(params.get("plan") || "monthly");
  const [bins, setBins] = useState(2);
  const [slots, setSlots] = useState([]);
  const [dayIdx, setDayIdx] = useState(0);
  const [slotDate, setSlotDate] = useState(null);
  const [slotTime, setSlotTime] = useState(null);
  const [slotLabel, setSlotLabel] = useState("");
  const [details, setDetails] = useState({
    name: "", phone: "", email: "", address: "", zip: "", pickup_day: "", notes: "",
  });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outOfArea, setOutOfArea] = useState(null);
  const [ack, setAck] = useState(false);

  const selectedPlan = planById(plan);
  const extraBins = Math.max(0, bins - 2);
  const isRecurring = plan !== "onetime";
  const perVisit = (plan === "monthly" ? selectedPlan.foundingPrice : selectedPlan.price) + extraBins * selectedPlan.extra;

  const WEEKDAYS = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4 };  // eslint-disable-line no-unused-vars

  const loadSlots = useCallback(async (weekday) => {
    setLoadingSlots(true);
    try {
      const params = weekday != null ? { weekday } : {};
      const { data } = await api.get("/slots", { params });
      setSlots(data.slots || []);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (step === 2) {
      setDayIdx(0);
      loadSlots();
    }
    // eslint-disable-next-line
  }, [step]);

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canContinue = () => {
    if (step === 0) return !!plan;
    if (step === 1)
      return details.name && details.phone && details.email && details.address && details.zip.length >= 5;
    if (step === 2) return !!slotDate && !!slotTime;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setOutOfArea(null);
    try {
      const { data } = await api.post("/bookings/checkout", {
        plan,
        num_bins: bins,
        slot_date: slotDate,
        slot_time: slotTime,
        referral_code: referralCode || undefined,
        ...details,
        origin_url: window.location.origin,
      });
      if (data.out_of_area) {
        setOutOfArea(data.message);
      } else if (data.checkout_url) {
        externalRedirect(data.checkout_url);
      }
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail, "Could not start checkout. Try again."));
      if (err.response?.status === 409) loadSlots();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">Book your cleaning</h1>
      <p className="mt-2 text-muted-foreground">Four quick steps — about a minute.</p>
      {referralCode && (
        <div data-testid="referral-banner" className="mt-4 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground">
          🎁 You were referred by a neighbor — booking helps them earn a free visit!
        </div>
      )}

      {/* Progress */}
      <div className="mt-8 flex items-center gap-2">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className="flex flex-1 items-center gap-2">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors ${
              i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}>
              {i < step ? <CheckCircle2 className="h-5 w-5" /> : i + 1}
            </div>
            <span className={`hidden text-sm font-semibold sm:block ${i <= step ? "text-foreground" : "text-muted-foreground"}`}>
              {label}
            </span>
            {i < 3 && <div className={`h-0.5 flex-1 ${i < step ? "bg-primary" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-8">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>
            {/* STEP 0: PLAN */}
            {step === 0 && (
              <div className="space-y-4">
                {PLANS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`booking-plan-${p.id}`}
                    onClick={() => setPlan(p.id)}
                    className={`w-full rounded-2xl border p-6 text-left transition-all ${
                      plan === p.id ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading text-lg font-bold">{p.name}</h3>
                        <p className="text-sm text-muted-foreground">{p.cadence} · {p.minimum}</p>
                      </div>
                      <div className="text-right">
                        <div className="font-heading text-2xl font-extrabold">${p.price}<span className="text-sm font-normal text-muted-foreground">{p.unit}</span></div>
                        {p.id === "monthly" && <div className="text-xs font-semibold text-primary">${p.foundingPrice} first 3 visits</div>}
                      </div>
                    </div>
                  </button>
                ))}

                <div className="rounded-2xl border border-border bg-card p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-heading text-lg font-bold">How many bins?</h4>
                      <p className="text-sm text-muted-foreground">
                        Includes 2 bins · each additional +${selectedPlan.extra} {isRecurring ? "per visit" : "each"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button data-testid="bins-decrease" onClick={() => setBins((b) => Math.max(1, b - 1))}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-border hover:bg-accent">
                        <Minus className="h-4 w-4" />
                      </button>
                      <span data-testid="bins-count" className="w-8 text-center font-heading text-xl font-bold">{bins}</span>
                      <button data-testid="bins-increase" onClick={() => setBins((b) => Math.min(10, b + 1))}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-border hover:bg-accent">
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                    <span className="font-semibold">{isRecurring ? "Per-visit total" : "Total"}</span>
                    <span data-testid="per-visit-total" className="font-heading text-xl font-extrabold text-primary">
                      ${perVisit.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: SLOTS */}
            {step === 2 && (
              <div>
                <div className="mb-4 rounded-xl border border-primary/30 bg-accent px-4 py-3 text-sm text-accent-foreground">
                  <p className="font-semibold">💡 Tip: cleaning day is best on your trash day{details.pickup_day ? ` (${details.pickup_day})` : ""} — pick any day, but your bins should be empty.</p>
                  <p className="mt-1">Please note: we do <strong>not</strong> clean bins that still have trash bags inside.</p>
                </div>
                {loadingSlots ? (
                  <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                ) : slots.length === 0 ? (
                  <p className="py-12 text-center text-muted-foreground">No open slots in the next few weeks. Please check back soon.</p>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <button data-testid="day-prev" onClick={() => setDayIdx((i) => Math.max(0, i - 1))}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-border hover:bg-accent disabled:opacity-40" disabled={dayIdx === 0}>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <div className="flex-1 overflow-x-auto">
                        <div className="flex gap-2">
                          {slots.map((d, i) => (
                            <button
                              key={d.date}
                              data-testid={`day-tab-${i}`}
                              onClick={() => setDayIdx(i)}
                              className={`shrink-0 rounded-xl border px-4 py-3 text-center transition-all ${
                                i === dayIdx ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"
                              }`}
                            >
                              <Calendar className="mx-auto h-4 w-4" />
                              <div className="mt-1 text-sm font-bold">{d.display}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <button data-testid="day-next" onClick={() => setDayIdx((i) => Math.min(slots.length - 1, i + 1))}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-border hover:bg-accent disabled:opacity-40" disabled={dayIdx >= slots.length - 1}>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      {slots[dayIdx]?.times.map((t) => {
                        const disabled = t.available <= 0;
                        const active = slotDate === slots[dayIdx].date && slotTime === t.time;
                        return (
                          <button
                            key={t.time}
                            data-testid={`slot-${slots[dayIdx].date}-${t.time}`}
                            disabled={disabled}
                            onClick={() => { setSlotDate(slots[dayIdx].date); setSlotTime(t.time); setSlotLabel(t.label); }}
                            className={`flex items-center justify-between rounded-xl border p-4 text-left transition-all ${
                              disabled ? "cursor-not-allowed border-border bg-muted/40 opacity-60"
                                : active ? "border-primary ring-2 ring-primary"
                                : "border-border hover:border-primary/50"
                            }`}
                          >
                            <span className="font-semibold">{t.label}</span>
                            <span className={`text-xs font-bold ${disabled ? "text-destructive" : "text-primary"}`}>
                              {disabled ? "Full" : `${t.available} left`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {slotDate && (
                      <p className="mt-4 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground">
                        Selected: {slots[dayIdx]?.display} · {slotLabel}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* STEP 1: DETAILS */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name" testid="detail-name" value={details.name} onChange={(v) => setDetails({ ...details, name: v })} />
                  <Field label="Phone" testid="detail-phone" value={details.phone} onChange={(v) => setDetails({ ...details, phone: v })} />
                </div>
                <Field label="Email" testid="detail-email" type="email" value={details.email} onChange={(v) => setDetails({ ...details, email: v })} />
                <AddressAutocomplete value={details.address}
                  onChange={(v) => setDetails({ ...details, address: v })}
                  onPick={(s) => setDetails({ ...details, address: s.address, zip: (s.zip || details.zip).slice(0, 5) })} />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="ZIP code" testid="detail-zip" value={details.zip}
                    onChange={(v) => setDetails({ ...details, zip: v.replace(/\D/g, "").slice(0, 5) })} />
                  <div>
                    <label className="text-sm font-semibold">Trash pickup day</label>
                    <select data-testid="detail-pickup" value={details.pickup_day}
                      onChange={(e) => setDetails({ ...details, pickup_day: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary">
                      <option value="">Select a day</option>
                      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((d) => <option key={d}>{d}</option>)}
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">Cleaning day is the same day as trash day — we suggest booking your visit on this day (bins should be empty, no trash bags inside).</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold">Gate code / bin color / size notes</label>
                  <textarea data-testid="detail-notes" rows={3} value={details.notes}
                    onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                    placeholder="e.g. Gate code 1234, green 96-gal bin behind fence"
                    className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
            )}

            {/* STEP 3: REVIEW / PAY */}
            {step === 3 && (
              <div>
                {outOfArea ? (
                  <div data-testid="out-of-area-message" className="rounded-2xl border border-destructive/40 bg-destructive/10 p-8 text-center">
                    <MapPin className="mx-auto h-10 w-10 text-destructive" />
                    <h3 className="mt-4 font-heading text-xl font-bold">We're not in your area — yet</h3>
                    <p className="mt-2 text-muted-foreground">{outOfArea}</p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border bg-card p-8">
                    <h3 className="font-heading text-xl font-bold">Review your booking</h3>
                    <dl className="mt-5 space-y-3 text-sm">
                      <Row k="Plan" v={selectedPlan.name} />
                      <Row k="Bins" v={bins} />
                      <Row k="First visit" v={`${slots[dayIdx]?.display || slotDate} · ${slotLabel}`} />
                      <Row k="Name" v={details.name} />
                      <Row k="Address" v={`${details.address}, ${details.zip}`} />
                      {details.pickup_day && <Row k="Pickup day" v={details.pickup_day} />}
                      <div className="flex justify-between border-t border-border pt-3">
                        <span className="font-bold">{isRecurring ? "Charged per visit" : "Total"}</span>
                        <span className="font-heading text-xl font-extrabold text-primary">${perVisit.toFixed(2)}</span>
                      </div>
                    </dl>
                    {plan === "monthly" && (
                      <p className="mt-4 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground">
                        🎉 Founding Special applied — first 3 visits at $29.99.
                      </p>
                    )}
                    <label data-testid="ack-label" className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/40 p-4 text-sm">
                      <input data-testid="ack-checkbox" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                        className="mt-0.5 h-5 w-5 shrink-0 accent-[hsl(var(--primary))]" />
                      <span>I understand my <strong>bins must be empty</strong> when the crew arrives. Bins Plus Pressure Washing does <strong>not</strong> clean bins that still contain trash bags — if bags are present, my service will need to be rescheduled.</span>
                    </label>
                    <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                      <ShieldCheck className="h-4 w-4 text-primary" /> Secure payment via Stripe. {isRecurring && "Your card is saved for automatic renewals."}
                    </p>
                    {isRecurring && !user ? (
                      <BookingAuthGate prefill={details} ackOk={ack} />
                    ) : (
                      <button data-testid="pay-button" onClick={submit} disabled={submitting || !ack}
                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-7 py-4 font-bold text-primary-foreground transition-all hover:brightness-110 disabled:opacity-50">
                        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : `Pay & Confirm — $${perVisit.toFixed(2)}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Nav buttons */}
      {!(step === 3 && outOfArea) && (
        <div className="mt-8 flex items-center justify-between">
          <button data-testid="booking-back" onClick={back} disabled={step === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3 font-semibold transition-colors hover:bg-accent disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < 3 && (
            <button data-testid="booking-next" onClick={next} disabled={!canContinue()}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 font-bold text-primary-foreground transition-all hover:brightness-110 disabled:opacity-40">
              Continue <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="font-semibold">{v}</dd>
    </div>
  );
}

function AddressAutocomplete({ value, onChange, onPick }) {
  const [suggests, setSuggests] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef();

  const handle = (v) => {
    onChange(v);
    clearTimeout(timer.current);
    if (v.trim().length < 3) { setSuggests([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/address/suggest", { params: { q: v } });
        setSuggests(data.suggestions || []);
        setOpen(true);
      } catch (err) { if (process.env.NODE_ENV === "development") console.error("Address suggestions failed:", err); } finally { setLoading(false); }
    }, 350);
  };

  return (
    <div className="relative">
      <label className="text-sm font-semibold">Street address</label>
      <input data-testid="detail-address" value={value} autoComplete="off"
        placeholder="Start typing your address…"
        onChange={(e) => handle(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
      {loading && <Loader2 className="absolute right-3 top-9 h-4 w-4 animate-spin text-muted-foreground" />}
      {open && suggests.length > 0 && (
        <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-border bg-card shadow-lg">
          {suggests.map((s, i) => (
            <li key={s.place_id || `${s.label}-${i}`}>
              <button type="button" data-testid={`address-suggestion-${i}`}
                onMouseDown={(e) => { e.preventDefault(); onPick(s); setOpen(false); }}
                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm hover:bg-accent">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span>{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-xs text-muted-foreground">USPS-style address suggestions as you type.</p>
    </div>
  );
}

function BookingAuthGate({ prefill, ackOk }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("signup");
  const [email, setEmail] = useState(prefill.email || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(prefill.name || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const go = async () => {
    setBusy(true);
    setErr("");
    try {
      if (mode === "signup") {
        await register({ name: name || prefill.name, email, password, phone: prefill.phone });
      } else {
        await login(email, password);
      }
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail, "Please check your details."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="booking-auth-gate" className="mt-6 rounded-xl border border-primary bg-accent/50 p-5">
      <p className="font-heading font-bold text-foreground">
        {mode === "signup" ? "Create an account to start your subscription" : "Log in to continue"}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Recurring plans need an account so you can manage or cancel anytime.
      </p>
      <div className="mt-4 space-y-3">
        {mode === "signup" && (
          <input data-testid="gate-name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-border bg-card px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
        )}
        <input data-testid="gate-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-border bg-card px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
        <input data-testid="gate-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-xl border border-border bg-card px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
        {err && <p data-testid="gate-error" className="text-sm font-semibold text-destructive">{err}</p>}
        <button data-testid="gate-submit" onClick={go} disabled={busy || !email || !password || !ackOk}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground transition-all hover:brightness-110 disabled:opacity-50">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "signup" ? "Create Account & Continue" : "Log In & Continue"}
        </button>
        <button data-testid="gate-toggle" onClick={() => setMode(mode === "signup" ? "login" : "signup")}
          className="w-full text-center text-sm font-semibold text-primary hover:underline">
          {mode === "signup" ? "Already have an account? Log in" : "Need an account? Sign up"}
        </button>
      </div>
    </div>
  );
}
